import { apiClient } from "../api/apiClient";
import {
    ConversationInput,
    ConversationMessage,
    ConversationResponse,
} from "../models/plan";

/**
 * ConversationService - Service for handling conversation-related operations
 */
export class ConversationService {
    /**
     * Send a conversation message
     * @param sessionId The session identifier
     * @param content The message content
     * @param skipRai Optional flag to skip RAI check for auto-generated messages
     * @returns Promise with the conversation response
     */
    static async sendMessage(
        sessionId: string,
        content: string,
        skipRai: boolean = false,
        fireAndForget: boolean = false
    ): Promise<ConversationResponse> {
        const input: ConversationInput = {
            session_id: sessionId,
            content: content,
            skip_rai: skipRai,
            fire_and_forget: fireAndForget || undefined,
        };

        try {
            const response = await apiClient.post(
                "/conversation/message",
                input
            );
            return response as ConversationResponse;
        } catch (error) {
            console.error("Error sending conversation message:", error);
            throw error;
        }
    }

    /**
     * Get conversation history for a session
     * @param sessionId The session identifier
     * @param limit Optional limit on number of messages
     * @returns Promise with array of conversation messages
     */
    static async getHistory(
        sessionId: string,
        limit?: number
    ): Promise<ConversationMessage[]> {
        try {
            const params = limit ? { limit } : undefined;
            const response = await apiClient.get(
                `/conversation/history/${sessionId}`,
                { params }
            );
            return response as ConversationMessage[];
        } catch (error: any) {
            if (error?.status !== 404) {
                console.error("Error getting conversation history:", error);
            }
            throw error;
        }
    }

    /**
     * Format timestamp for display
     * @param timestamp ISO timestamp string
     * @returns Formatted date/time string
     */
    static formatTimestamp(timestamp: string): string {
        try {
            return new Intl.DateTimeFormat(undefined, {
                dateStyle: "short",
                timeStyle: "short",
            }).format(new Date(timestamp));
        } catch (error) {
            return timestamp;
        }
    }
}
